import { useState } from "react";
import { api } from "@/lib/api";
import { ReportShell, Stat, Table, money } from "./ReportShell";

const ymd = (d) => d.toISOString().slice(0, 10);
const yearStart = () => ymd(new Date(new Date().getFullYear(), 0, 1));

export const ReferralsReport = () => {
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(ymd(new Date()));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/referrals", { params: { from, to } });
      setData(data);
      setSel(data?.rows?.[0]?.referrer_id || null);
    } finally {
      setBusy(false);
    }
  };

  const t = data?.totals;
  const rows = data?.rows || [];
  const selRow = rows.find((r) => r.referrer_id === sel);
  const referred = selRow?.referred || [];
  const csvRows = rows.map((r) => ({
    referrer: r.referrer,
    referred_accounts: r.referred_count,
    buying_accounts: r.active_count,
    invoices: r.invoices,
    sales_volume: r.invoiced,
  }));

  return (
    <ReportShell
      id="referrals"
      title="Referrals Report"
      blurb="Sales volume grouped by the account that referred the customer — invoiced amount, voided invoices excluded."
      range
      from={from}
      to={to}
      onFrom={setFrom}
      onTo={setTo}
      onGenerate={generate}
      busy={busy}
      generated={!!data}
      csvRows={csvRows}
      csvName={`referrals-${from}-${to}.csv`}
    >
      {t && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Stat label="Referring accounts" value={t.referrers} testId="referrals-stat-referrers" />
          <Stat label="Referred accounts" value={t.referred} testId="referrals-stat-referred" />
          <Stat label="Referred sales" value={money(t.invoiced)} tone="text-yellow-400" testId="referrals-stat-invoiced" />
        </div>
      )}
      <div className="mt-4 grid gap-4 lg:grid-cols-[3fr_2fr]">
        <Table
          testId="referrals-referrers"
          rowKey={(r) => r.referrer_id}
          rows={rows}
          empty="No referrals in this range — set a “Referred by” account on a customer first."
          columns={[
            {
              key: "referrer",
              label: "Referred by",
              render: (r) => (
                <button
                  type="button"
                  data-testid={`referrals-row-${r.referrer_id}`}
                  onClick={() => setSel(r.referrer_id)}
                  className={`text-left font-semibold ${r.referrer_id === sel ? "text-yellow-400" : "hover:text-yellow-300"}`}
                >
                  {r.referrer}
                </button>
              ),
            },
            { key: "referred_count", label: "Referred", right: true },
            {
              key: "active_count",
              label: "Buying",
              right: true,
              render: (r) => <span className="text-emerald-400">{r.active_count}</span>,
            },
            { key: "invoiced", label: "Sales volume", right: true, render: (r) => money(r.invoiced) },
          ]}
        />
        <Table
          testId="referrals-detail"
          rowKey={(r) => r.id}
          rows={referred}
          empty={selRow ? "This account's referrals have no invoices in range." : "Pick a referring account."}
          columns={[
            {
              key: "name",
              label: selRow ? `Referred by ${selRow.referrer}` : "Referred accounts",
              render: (r) => <span className="font-semibold">{r.name}</span>,
            },
            { key: "invoices", label: "#", right: true },
            { key: "invoiced", label: "Invoiced", right: true, render: (r) => money(r.invoiced) },
          ]}
        />
      </div>
    </ReportShell>
  );
};
